// src/services/mock/mockInterceptor.ts

import { RequestConfig } from '../api/client';
import { ApiResponse, ApiError } from '../api/types';
import { qualityManagementMockData } from './mockData';
import { mockUsers, getUserById, getUserByUsername } from '../../../shared/mock/mockData';
import { apiConfig, endpoints } from '../api/config';

// Mock JWT tokens
const MOCK_ACCESS_TOKEN = 'mock-jwt-access-token-12345';
const MOCK_REFRESH_TOKEN = 'mock-jwt-refresh-token-67890';

// Simulate network delay
const delay = (ms: number = apiConfig.mockDelay) => 
  new Promise(resolve => setTimeout(resolve, ms));

// Use shared user session - this should be set by the global auth
let currentUser: any = null;

export class MockInterceptor {
  // Set current user from global auth context
  setCurrentUser(user: any) {
    currentUser = user;
  }

  async handleRequest<T = any>(config: RequestConfig): Promise<ApiResponse<T>> {
    await delay();

    const { method, endpoint, data, params } = config;
    
    try {
      // Authentication endpoints (these should actually be handled by shared auth)
      if (endpoint === endpoints.auth.login && method === 'POST') {
        return this.handleLogin(data);
      }
      
      if (endpoint === endpoints.auth.refresh && method === 'POST') {
        return this.handleRefreshToken(data);
      }
      
      if (endpoint === endpoints.auth.logout && method === 'POST') {
        return this.handleLogout();
      }
      
      if (endpoint === endpoints.auth.me && method === 'GET') {
        return this.handleGetCurrentUser();
      }

      // Protected endpoints require authentication
      this.requireAuth();

      // User endpoints (use shared users)
      if (endpoint === endpoints.users.list && method === 'GET') {
        return this.handleGetUsers();
      }

      if (endpoint.startsWith('/users/') && method === 'GET') {
        const userId = this.extractIdFromPath(endpoint, '/users/');
        return this.handleGetUser(userId);
      }

      // Project endpoints
      if (endpoint === endpoints.projects.list && method === 'GET') {
        return this.handleGetProjects();
      }
      
      if (endpoint === endpoints.projects.create && method === 'POST') {
        return this.handleCreateProject(data);
      }
      
      if (endpoint.startsWith('/projects/') && endpoint.endsWith('/tasks') && method === 'GET') {
        const projectId = this.extractIdFromPath(endpoint, '/projects/', '/tasks');
        return this.handleGetProjectTasks(projectId, params);
      }
      
      if (endpoint.startsWith('/projects/') && endpoint.endsWith('/tasks') && method === 'POST') {
        const projectId = this.extractIdFromPath(endpoint, '/projects/', '/tasks');
        return this.handleCreateTask(projectId, data);
      }
      
      if (endpoint.startsWith('/projects/') && method === 'GET') {
        const projectId = this.extractIdFromPath(endpoint, '/projects/');
        return this.handleGetProject(projectId);
      }

      // Task endpoints
      if (endpoint.startsWith('/tasks/') && endpoint.endsWith('/complete') && method === 'PATCH') {
        const taskId = this.extractIdFromPath(endpoint, '/tasks/', '/complete');
        return this.handleCompleteTask(taskId);
      }
      
      if (endpoint.startsWith('/tasks/') && endpoint.endsWith('/time-entries') && method === 'GET') {
        const taskId = this.extractIdFromPath(endpoint, '/tasks/', '/time-entries');
        return this.handleGetTaskTimeEntries(taskId);
      }
      
      if (endpoint.startsWith('/tasks/') && method === 'GET') {
        const taskId = this.extractIdFromPath(endpoint, '/tasks/');
        return this.handleGetTask(taskId);
      }
      
      if (endpoint.startsWith('/tasks/') && method === 'PUT') {
        const taskId = this.extractIdFromPath(endpoint, '/tasks/');
        return this.handleUpdateTask(taskId, data);
      }
      
      if (endpoint.startsWith('/tasks/') && method === 'DELETE') {
        const taskId = this.extractIdFromPath(endpoint, '/tasks/');
        return this.handleDeleteTask(taskId);
      }

      if (endpoint.startsWith('/users/') && endpoint.endsWith('/tasks') && method === 'GET') {
        const userId = this.extractIdFromPath(endpoint, '/users/', '/tasks');
        return this.handleGetUserTasks(userId);
      }

      // Time entry endpoints
      if (endpoint === endpoints.timeEntries.list && method === 'GET') {
        return this.handleGetTimeEntries(params);
      }
      
      if (endpoint === endpoints.timeEntries.create && method === 'POST') {
        return this.handleCreateTimeEntry(data);
      }
      
      if (endpoint.startsWith('/time-entries/') && method === 'PUT') {
        const entryId = this.extractIdFromPath(endpoint, '/time-entries/');
        return this.handleUpdateTimeEntry(entryId, data);
      }
      
      if (endpoint.startsWith('/time-entries/') && method === 'DELETE') {
        const entryId = this.extractIdFromPath(endpoint, '/time-entries/');
        return this.handleDeleteTimeEntry(entryId);
      }

      if (endpoint.startsWith('/users/') && endpoint.endsWith('/time-entries') && method === 'GET') {
        const userId = this.extractIdFromPath(endpoint, '/users/', '/time-entries');
        return this.handleGetUserTimeEntries(userId, params);
      }

      // If no endpoint matches, return 404
      throw this.createError('Endpoint not found', 404);
      
    } catch (error) {
      console.error('MockInterceptor error:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        error: error
      });
      if (error instanceof Error && 'status' in error) {
        throw error; // Re-throw API errors
      }
      
      // Handle unexpected errors
      throw this.createError('Internal server error', 500);
    }
  }

  // Helper method to convert shared user to API user format
  private mapSharedUserToApiUser(sharedUser: any): any {
    return {
      id: sharedUser.id,
      name: sharedUser.name,
      email: sharedUser.email,
      role: sharedUser.role === 'manager' ? 'Project Manager' : 
            sharedUser.role === 'employee' ? 'Employee' : 
            sharedUser.role,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  }
  private extractIdFromPath(path: string, prefix: string, suffix: string = ''): number {
    const withoutPrefix = path.slice(prefix.length);
    const withoutSuffix = suffix ? withoutPrefix.slice(0, -suffix.length) : withoutPrefix;
    return parseInt(withoutSuffix, 10);
  }

  private requireAuth() {
    if (!currentUser) {
      throw this.createError('Authentication required', 401);
    }
  }

  private requireRole(role: string) {
    this.requireAuth();
    if (currentUser.role !== role) {
      throw this.createError('Insufficient permissions', 403);
    }
  }

  private createError(message: string, status: number): ApiError {
    return {
      success: false,
      message,
      status,
    };
  }

  private createResponse<T>(data: T, message?: string): ApiResponse<T> {
    return {
      success: true,
      data,
      message,
    };
  }

  // Authentication handlers (these should ideally be handled by global auth)
  private async handleLogin(credentials: any): Promise<ApiResponse<any>> {
    const user = getUserByUsername(credentials.username);

    if (!user) {
      throw this.createError('Invalid credentials', 401);
    }

    currentUser = user;

    return this.createResponse({
      access_token: MOCK_ACCESS_TOKEN,
      refresh_token: MOCK_REFRESH_TOKEN,
      token_type: 'Bearer',
      expires_in: 3600,
      user,
    }, 'Login successful');
  }

  private async handleRefreshToken(data: any): Promise<ApiResponse<any>> {
    if (data.refresh_token !== MOCK_REFRESH_TOKEN) {
      throw this.createError('Invalid refresh token', 401);
    }

    return this.createResponse({
      access_token: MOCK_ACCESS_TOKEN,
      refresh_token: MOCK_REFRESH_TOKEN,
      token_type: 'Bearer',
      expires_in: 3600,
    }, 'Token refreshed');
  }

  private async handleLogout(): Promise<ApiResponse<any>> {
    currentUser = null;
    return this.createResponse(null, 'Logout successful');
  }

  private async handleGetCurrentUser(): Promise<ApiResponse<any>> {
    this.requireAuth();
    return this.createResponse(currentUser);
  }

  // User handlers (use shared users)
  private async handleGetUsers(): Promise<ApiResponse<any>> {
    // Only managers can see department users for project assignment
    if (currentUser.role !== 'manager' && currentUser.role !== 'admin') {
      throw this.createError('Insufficient permissions', 403);
    }

    // Get users available for project assignment
    const availableUsers = qualityManagementMockData.getAvailableUsersForProject(currentUser.id);
    // Map shared users to API format
    const apiUsers = availableUsers.map(user => this.mapSharedUserToApiUser(user));
    return this.createResponse(apiUsers);
  }

  private async handleGetUser(userId: number): Promise<ApiResponse<any>> {
    const user = getUserById(userId);
    if (!user) {
      throw this.createError('User not found', 404);
    }
    // Map shared user to API format
    const apiUser = this.mapSharedUserToApiUser(user);
    return this.createResponse(apiUser);
  }

  // Project handlers
  private async handleGetProjects(): Promise<ApiResponse<any>> {
    const projects = qualityManagementMockData.getProjectsForUser(currentUser.id);
    return this.createResponse(projects);
  }

  private async handleCreateProject(data: any): Promise<ApiResponse<any>> {
    this.requireRole('manager');
    
    // Create new project with new ID
    const newId = Math.max(...qualityManagementMockData.projects.map(p => p.id)) + 1;
    const newProject = {
      id: newId,
      name: data.name,
      description: data.description,
      assigned_employee_ids: data.assigned_employee_ids || [],
      created_by: currentUser.id,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    // Add to mock data (in a real app, this would persist)
    qualityManagementMockData.projects.push(newProject);
    
    return this.createResponse(newProject, 'Project created successfully');
  }

  private async handleGetProject(projectId: number): Promise<ApiResponse<any>> {
    const project = qualityManagementMockData.projects.find(p => p.id === projectId);
    if (!project) {
      throw this.createError('Project not found', 404);
    }

    // Check access
    const userProjects = qualityManagementMockData.getProjectsForUser(currentUser.id);
    if (!userProjects.find(p => p.id === projectId)) {
      throw this.createError('Access denied', 403);
    }

    return this.createResponse(project);
  }

  // Task handlers
  private async handleGetProjectTasks(projectId: number, params?: any): Promise<ApiResponse<any>> {
    // Check if project exists and user has access
    const userProjects = qualityManagementMockData.getProjectsForUser(currentUser.id);
    if (!userProjects.find(p => p.id === projectId)) {
      throw this.createError('Project not found or access denied', 404);
    }

    let tasks;

    // If user_id is specified in params, get tasks for that specific user
    if (params && params.user_id) {
      const userId = parseInt(params.user_id);
      tasks = qualityManagementMockData.getTasksByUserAndProject(userId, projectId);
    } else {
      // Get all tasks for the project (filtered by user role)
      if (currentUser.role === 'manager') {
        // Managers see all project tasks
        tasks = qualityManagementMockData.tasks.filter(t => t.project_id === projectId);
      } else {
        // Employees see only their assigned tasks
        tasks = qualityManagementMockData.getTasksByUserAndProject(currentUser.id, projectId);
      }
    }

    // Filter by status if specified
    if (params && params.status) {
      tasks = tasks.filter(task => task.status === params.status);
    }

    return this.createResponse(tasks);
  }

  private async handleCreateTask(projectId: number, data: any): Promise<ApiResponse<any>> {
    // Check if project exists and user has access
    const userProjects = qualityManagementMockData.getProjectsForUser(currentUser.id);
    const project = userProjects.find(p => p.id === projectId);
    if (!project) {
      throw this.createError('Project not found or access denied', 404);
    }

    // Create new task
    const newId = Math.max(...qualityManagementMockData.tasks.map(t => t.id)) + 1;
    const newTask = {
      id: newId,
      project_id: projectId,
      name: data.name,
      description: data.description,
      assigned_employee_ids: data.assigned_employee_ids || [currentUser.id],
      created_by: currentUser.id,
      status: 'pending' as const,
      created_date: new Date().toISOString().split('T')[0],
      task_date: data.task_date || new Date().toISOString().split('T')[0],
      total_hours: 0,
      expected_hours: data.expected_hours || 0,
      completion_percentage: data.completion_percentage || 0
    };

    // Add to mock data
    qualityManagementMockData.tasks.push(newTask);

    return this.createResponse(newTask, 'Task created successfully');
  }

  private async handleGetTask(taskId: number): Promise<ApiResponse<any>> {
    const task = qualityManagementMockData.tasks.find(t => t.id === taskId);
    if (!task) {
      throw this.createError('Task not found', 404);
    }

    // Check access
    const userTasks = qualityManagementMockData.getTasksForUser(currentUser.id);
    if (!userTasks.find(t => t.id === taskId)) {
      throw this.createError('Access denied', 403);
    }

    return this.createResponse(task);
  }

  private async handleUpdateTask(taskId: number, data: any): Promise<ApiResponse<any>> {
    const taskIndex = qualityManagementMockData.tasks.findIndex(t => t.id === taskId);
    if (taskIndex === -1) {
      throw this.createError('Task not found', 404);
    }

    const task = qualityManagementMockData.tasks[taskIndex];

    // Check if user can edit this task
    if (currentUser.role !== 'manager' && task.created_by !== currentUser.id) {
      throw this.createError('Access denied', 403);
    }

    // Update task
    const updatedTask = {
      ...task,
      ...data,
      id: taskId, // Ensure ID doesn't change
      project_id: task.project_id, // Ensure project_id doesn't change
      created_by: task.created_by, // Ensure created_by doesn't change
      created_date: task.created_date // Ensure created_date doesn't change
    };

    qualityManagementMockData.tasks[taskIndex] = updatedTask;

    return this.createResponse(updatedTask, 'Task updated successfully');
  }

  private async handleDeleteTask(taskId: number): Promise<ApiResponse<any>> {
    const taskIndex = qualityManagementMockData.tasks.findIndex(t => t.id === taskId);
    if (taskIndex === -1) {
      throw this.createError('Task not found', 404);
    }

    const task = qualityManagementMockData.tasks[taskIndex];

    // Check if user can delete this task
    if (currentUser.role !== 'manager' && task.created_by !== currentUser.id) {
      throw this.createError('Access denied', 403);
    }

    // Remove task
    qualityManagementMockData.tasks.splice(taskIndex, 1);

    return this.createResponse(null, 'Task deleted successfully');
  }

  private async handleCompleteTask(taskId: number): Promise<ApiResponse<any>> {
    const taskIndex = qualityManagementMockData.tasks.findIndex(t => t.id === taskId);
    if (taskIndex === -1) {
      throw this.createError('Task not found', 404);
    }

    const task = qualityManagementMockData.tasks[taskIndex];

    // Check if user is assigned to this task
    if (!task.assigned_employee_ids.includes(currentUser.id)) {
      throw this.createError('Access denied', 403);
    }

    // Complete task
    const completedTask = {
      ...task,
      status: 'completed' as const,
      completed_date: new Date().toISOString().split('T')[0]
    };

    qualityManagementMockData.tasks[taskIndex] = completedTask;

    return this.createResponse(completedTask, 'Task completed successfully');
  }

  private async handleGetUserTasks(userId: number): Promise<ApiResponse<any>> {
    // Users can only get their own tasks, managers can get any user's tasks
    if (currentUser.role !== 'manager' && currentUser.id !== userId) {
      throw this.createError('Access denied', 403);
    }

    const tasks = qualityManagementMockData.getTasksForUser(userId);
    const projects = qualityManagementMockData.projects;
    
    const tasksWithProjects = tasks.map(task => {
      const project = projects.find(p => p.id === task.project_id);
      return { task, project };
    });

    return this.createResponse(tasksWithProjects);
  }

  private async handleGetTaskTimeEntries(taskId: number): Promise<ApiResponse<any>> {
    const task = qualityManagementMockData.tasks.find(t => t.id === taskId);
    if (!task) {
      throw this.createError('Task not found', 404);
    }

    const timeEntries = qualityManagementMockData.timeEntries.filter(te => te.task_id === taskId);
    return this.createResponse(timeEntries);
  }

  // Time entry handlers
  private async handleGetTimeEntries(params: any = {}): Promise<ApiResponse<any>> {
    let timeEntries = qualityManagementMockData.timeEntries;

    // Filter by user (employees can only see their own)
    if (currentUser.role !== 'manager') {
      timeEntries = timeEntries.filter(te => te.employee_id === currentUser.id);
    } else if (params.user_id) {
      timeEntries = timeEntries.filter(te => te.employee_id === parseInt(params.user_id));
    }

    // Filter by date range
    if (params.start_date) {
      timeEntries = timeEntries.filter(te => te.date >= params.start_date);
    }
    if (params.end_date) {
      timeEntries = timeEntries.filter(te => te.date <= params.end_date);
    }

    // Filter by project
    if (params.project_id) {
      timeEntries = timeEntries.filter(te => te.project_id === parseInt(params.project_id));
    }

    // Filter by task
    if (params.task_id) {
      timeEntries = timeEntries.filter(te => te.task_id === parseInt(params.task_id));
    }

    return this.createResponse(timeEntries);
  }

  private async handleCreateTimeEntry(data: any): Promise<ApiResponse<any>> {
    // Verify task exists and user has access
    const task = qualityManagementMockData.tasks.find(t => t.id === data.task_id);
    if (!task) {
      throw this.createError('Task not found', 404);
    }

    if (!task.assigned_employee_ids.includes(currentUser.id)) {
      throw this.createError('Access denied - not assigned to this task', 403);
    }

    // Create new time entry
    const newId = Math.max(...qualityManagementMockData.timeEntries.map(te => te.id)) + 1;
    const newTimeEntry = {
      id: newId,
      employee_id: currentUser.id,
      task_id: data.task_id,
      project_id: data.project_id,
      date: data.date,
      hours: data.hours,
      description: data.description || '',
      created_at: new Date().toISOString()
    };

    // Add to mock data
    qualityManagementMockData.timeEntries.push(newTimeEntry);

    return this.createResponse(newTimeEntry, 'Time entry logged successfully');
  }

  private async handleUpdateTimeEntry(entryId: number, data: any): Promise<ApiResponse<any>> {
    const entryIndex = qualityManagementMockData.timeEntries.findIndex(te => te.id === entryId);
    if (entryIndex === -1) {
      throw this.createError('Time entry not found', 404);
    }

    const entry = qualityManagementMockData.timeEntries[entryIndex];

    // Users can only update their own entries
    if (entry.employee_id !== currentUser.id) {
      throw this.createError('Access denied', 403);
    }

    // Update entry
    const updatedEntry = {
      ...entry,
      ...data,
      id: entryId, // Ensure ID doesn't change
      employee_id: entry.employee_id, // Ensure employee_id doesn't change
      created_at: entry.created_at // Ensure created_at doesn't change
    };

    qualityManagementMockData.timeEntries[entryIndex] = updatedEntry;

    return this.createResponse(updatedEntry, 'Time entry updated successfully');
  }

  private async handleDeleteTimeEntry(entryId: number): Promise<ApiResponse<any>> {
    const entryIndex = qualityManagementMockData.timeEntries.findIndex(te => te.id === entryId);
    if (entryIndex === -1) {
      throw this.createError('Time entry not found', 404);
    }

    const entry = qualityManagementMockData.timeEntries[entryIndex];

    // Users can only delete their own entries
    if (entry.employee_id !== currentUser.id) {
      throw this.createError('Access denied', 403);
    }

    // Remove entry
    qualityManagementMockData.timeEntries.splice(entryIndex, 1);

    return this.createResponse(null, 'Time entry deleted successfully');
  }

  private async handleGetUserTimeEntries(userId: number, params: any = {}): Promise<ApiResponse<any>> {
    // Users can only get their own time entries, managers can get any user's entries
    if (currentUser.role !== 'manager' && currentUser.id !== userId) {
      throw this.createError('Access denied', 403);
    }

    const timeEntries = qualityManagementMockData.getTimeEntriesForUser(
      userId,
      params.start_date,
      params.end_date
    );

    return this.createResponse(timeEntries);
  }
}

// Export singleton instance
export const mockInterceptor = new MockInterceptor();
